/**
 * EditorialIntro — shared aspect. The editorial-voice opening block
 * for a route hero: amber kicker, large Cormorant display headline
 * with an optional italic-accent span, DM Sans subtitle, optional
 * children slot, and an optional amber CTA pill with hover lift.
 *
 * Used by /brand Aspect 23 AND by the Student home page's
 * DashboardHero left column. Designed to be flexible enough for
 * other pages' editorial intros (Coming Soon, FAQ, Contact, etc.)
 * without forking — same kicker/headline/subtitle/CTA rhythm.
 */
import { useState } from "react";
import {
  Platform,
  Pressable,
  Text,
  View,
  type ViewStyle,
} from "react-native";
import Svg, { Path } from "react-native-svg";

import { COLORS, FONTS, SPACING } from "../../lib/brand";
import type { PaneTheme } from "../brand/paneThemes";

export type EditorialIntroHeadline =
  | string
  | {
      before: string;
      /** Rendered in italic Cormorant with accent color. */
      accent: string;
      after?: string;
    };

export type EditorialIntroCta = {
  label: string;
  onPress: () => void;
  accessibilityLabel?: string;
};

export type EditorialIntroProps = {
  theme: PaneTheme;
  kicker?: string;
  headline: EditorialIntroHeadline;
  subtitle?: string;
  /** Inserted between subtitle and CTA. Useful for contextual
   *  callouts (e.g. a graduation-countdown panel). */
  children?: React.ReactNode;
  cta?: EditorialIntroCta;
};

export function EditorialIntro({
  theme,
  kicker,
  headline,
  subtitle,
  children,
  cta,
}: EditorialIntroProps) {
  return (
    <View>
      {kicker ? (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 10,
            marginBottom: 10,
          }}
        >
          <View style={{ height: 1, width: 28, backgroundColor: theme.accent }} />
          <Text
            style={{
              fontFamily: theme.bodyBoldFont,
              fontSize: 10,
              letterSpacing: 2.2,
              textTransform: "uppercase",
              color: theme.accent,
            }}
          >
            {kicker}
          </Text>
        </View>
      ) : null}

      <Text
        style={{
          fontFamily: FONTS.display,
          fontSize: 44,
          lineHeight: 46,
          letterSpacing: -0.5,
          color: theme.textOnSurface,
          marginBottom: 8,
        }}
      >
        {typeof headline === "string" ? (
          headline
        ) : (
          <>
            {headline.before}
            <Text
              style={{
                fontFamily: FONTS.display,
                fontStyle: "italic",
                color: theme.accent,
              }}
            >
              {headline.accent}
            </Text>
            {headline.after ?? ""}
          </>
        )}
      </Text>

      {subtitle ? (
        <Text
          style={{
            fontFamily: theme.bodyFont,
            fontSize: 13.5,
            lineHeight: 21,
            color: theme.textOnSurfaceDim,
            marginBottom: 16,
            maxWidth: 540,
          }}
        >
          {subtitle}
        </Text>
      ) : null}

      {children}

      {cta ? <CtaPill theme={theme} cta={cta} /> : null}
    </View>
  );
}

function CtaPill({ theme, cta }: { theme: PaneTheme; cta: EditorialIntroCta }) {
  const [hover, setHover] = useState(false);
  const isWeb = Platform.OS === "web";
  return (
    <Pressable
      onPress={cta.onPress}
      accessibilityRole="button"
      accessibilityLabel={cta.accessibilityLabel ?? cta.label}
      {...(isWeb
        ? ({
            onMouseEnter: () => setHover(true),
            onMouseLeave: () => setHover(false),
          } as const)
        : {})}
      style={[
        {
          alignSelf: "flex-start",
          backgroundColor: hover ? COLORS.AMBER_WARM.hex : theme.accent,
          borderRadius: 5,
          paddingVertical: SPACING.sm,
          paddingHorizontal: 20,
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
        },
        isWeb
          ? ({
              cursor: "pointer",
              transition:
                "background-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease",
              boxShadow: hover
                ? "0 12px 24px -10px rgba(28,43,26,0.55)"
                : "0 8px 20px -10px rgba(28,43,26,0.45)",
              transform: [{ translateY: hover ? -1 : 0 }],
            } as unknown as ViewStyle)
          : null,
      ]}
    >
      <Text
        style={{
          fontFamily: theme.bodyBoldFont,
          fontSize: 11,
          letterSpacing: 1.76,
          textTransform: "uppercase",
          color: COLORS.FOREST.hex,
        }}
      >
        {cta.label}
      </Text>
      <Svg width={13} height={13} viewBox="0 0 12 12" fill="none">
        <Path
          d="M2.5 6h7M6.5 3l3 3-3 3"
          stroke={COLORS.FOREST.hex}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Svg>
    </Pressable>
  );
}
